import axios from 'axios';
import Cookies from 'js-cookie';
import { useContext } from 'react';

import AppContext from '../../../context';
import { Translations } from '../../../types';
import { withSentryHub } from '../../../utils/sentry';

import {
    HoppsSuggestionsApiCategoryItem,
    HoppsSuggestionsApiProductItem,
    HoppsSuggestionsApiPageItem,
    UiSuggestionSection,
    UiSuggestions,
    UiSuggestionItem,
    ZtNav,
    HoppsSuggestionsApiResponse,
} from './types';

const SUGGESTION_API_OPTIONS = {
    headers: {
        'X-Caller': 'shop-header',
    },
};

function convertCategory(
    categoryJson: HoppsSuggestionsApiCategoryItem,
    term: string,
    translations: Translations,
    languagePrefix: string,
): UiSuggestionItem {
    const {
        parentCategoryTitle: parentGroupTitle,
        categoryTitle: groupTitle,
        count,
        path,
    } = categoryJson;

    const separator = translations.get(
        'shop.header.search.autocomplete.separator',
    );

    const title = parentGroupTitle
        ? `${groupTitle} ${separator} ${parentGroupTitle}`
        : groupTitle;

    const encodedSearchPath = encodeURIComponent(path);
    return {
        url: `${languagePrefix}/search/results?ct=${encodedSearchPath}&q=${term}`,
        title,
        count: count.toString(),
    };
}

function convertProduct(
    productJson: HoppsSuggestionsApiProductItem,
): UiSuggestionItem {
    const {
        path: url,
        pictureUrl: picture,
        name: title,
        isSponsored,
    } = productJson;
    return { url, title, picture, isSponsored };
}

function convertPage(pageJson: HoppsSuggestionsApiPageItem): UiSuggestionItem {
    const { targetUrl: url, display: title } = pageJson;
    return { url, title };
}

function convertSuggestions(
    term: string,
    responseJson: HoppsSuggestionsApiResponse,
    translations: Translations,
    languagePrefix: string,
): UiSuggestions {
    const suggestionItems: UiSuggestionSection[] = [];

    if (responseJson.categories) {
        suggestionItems.push({
            type: 'CATEGORY',
            items: responseJson.categories.map((data) =>
                convertCategory(data, term, translations, languagePrefix),
            ),
        });
    }

    if (responseJson.products) {
        suggestionItems.push({
            type: 'PRODUCT',
            items: responseJson.products.map((data) => convertProduct(data)),
        });
    }

    if (responseJson.pages) {
        suggestionItems.push({
            type: 'SERVICE',
            items: responseJson.pages.map((data) => convertPage(data)),
        });
    }

    return {
        suggestionItems,
        redirectUri: responseJson.redirectUri || null,
    };
}

function getLanguagePrefix(selectedShopLanguage: string | undefined): string {
    if (typeof selectedShopLanguage !== 'undefined') {
        return selectedShopLanguage === 'fr' ? '/fr' : '';
    }
    return '';
}

export function useSuggestSearchSupport() {
    const { hoppsSuggestionsUrl, translations, selectedShopLanguage } =
        useContext(AppContext);
    const languagePrefix = getLanguagePrefix(selectedShopLanguage);

    function redirectToSearchResults(term: string) {
        if (term.length > 0) {
            const encodedTerm = encodeURIComponent(term);
            window.location =
                `${languagePrefix}/search/results?q=${encodedTerm}` as any;
        }
    }

    function populateRvpsId(ztNav: string): string | undefined {
        try {
            const parsed: ZtNav[] = JSON.parse(ztNav);
            return parsed.map((product) => product.product_id).join(',');
        } catch (err) {
            return undefined;
        }
    }

    function redirectToServicePage(url: string) {
        window.location = url as any;
    }

    function buildQueryParams(term: string): string {
        const customerId = Cookies.get('cid');
        const sessionId = Cookies.get('sid');
        const rvPsIds = Cookies.get('zt_nav');

        let queryParams = `q=${term}`;

        if (customerId) {
            queryParams += `&customerId=${customerId}`;
        }

        if (sessionId) {
            queryParams += `&sid=${sessionId}`;
        }

        if (rvPsIds) {
            const convertedRvpsIds = populateRvpsId(rvPsIds);
            if (convertedRvpsIds) {
                queryParams += `&rvPsIds=${convertedRvpsIds}`;
            }
        }

        return queryParams;
    }

    async function fetchSuggestions(
        term: string,
    ): Promise<UiSuggestions | null> {
        const queryParams = buildQueryParams(term);

        try {
            const response = await axios.get(
                `${hoppsSuggestionsUrl}?${queryParams}`,
                SUGGESTION_API_OPTIONS,
            );
            if (response.data) {
                return convertSuggestions(
                    term,
                    response.data,
                    translations,
                    languagePrefix,
                );
            }
            return null;
        } catch (e) {
            withSentryHub()?.captureException(
                new Error(`Error fetching suggestions from API: ${e}`),
                {
                    captureContext: (scope) =>
                        scope.setTags({ type: 'searchEngineRequest' }),
                },
            );
            return null;
        }
    }

    return { redirectToSearchResults, fetchSuggestions, redirectToServicePage };
}

export const testExport = {
    SUGGESTION_API_OPTIONS,
};
